import { Button, IconClose } from '@odekoteam/doppio';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Grid, Heading, Input, Select } from 'theme-ui';
import { useClient } from 'urql';

import ConfigureLaunch from '@/components/Collection/ConfigureLaunch';
import CreativeAsset from '@/components/Collection/CreativeAsset';
import { ErrorLabel } from '@/components/Collection/Error';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import {
  formatComponentType,
  formatMasterHomepageRowType,
} from '@/components/Collection/utils';
import {
  CreateHomepageComponentInput,
  HomepageCollectionComponentType,
  MasterHomepageRowStructureType,
  useCreateHomepageComponentMutation,
  useGetCollectionSetupForEditQuery,
  useGetCollectionsPageQuery,
  useGetMasterHomepageForEditQuery,
  useGetWarehousesQuery,
} from '@/graphql/types/generated';
import { useAppDispatch } from '@/hooks/store';
import { useAllCollectionItems } from '@/hooks/useAllCollectionItems';
import { showToast, ToastAlertType } from '@/state/toast';
import { uploadImage } from '@/utils/uploadImage';
import { isDevelopment } from '@/utils/environment';

type CreativeAssetFormData = {
  image: string;
  fontStyle: string;
  webEndDate: string;
  webPosition: string;
  mobileImage: string;
  desktopImage: string;
  webStartDate: string;
  warehouseIds: string[];
};

const IMAGES_BY_COMPONENT: Record<
  HomepageCollectionComponentType,
  {
    title: string;
    images: {
      label: string;
      fieldName: keyof CreativeAssetFormData;
      requiredWidth: number;
      requiredHeight: number;
    }[];
  }
> = {
  [HomepageCollectionComponentType.CollectionBanner]: {
    title: 'Half-Banner',
    images: [
      {
        label: 'Desktop',
        fieldName: 'desktopImage',
        requiredWidth: 1120,
        requiredHeight: 400,
      },
      {
        label: 'Mobile',
        fieldName: 'mobileImage',
        requiredWidth: 630,
        requiredHeight: 390,
      },
    ],
  },
  [HomepageCollectionComponentType.CollectionCarousel]: {
    title: 'Carousel',
    images: [],
  },
  [HomepageCollectionComponentType.CollectionHighlightCarousel]: {
    title: 'Highlight Carousel',
    images: [
      {
        label: 'Highlight Image',
        fieldName: 'image',
        requiredWidth: 792,
        requiredHeight: 320,
      },
    ],
  },
  [HomepageCollectionComponentType.CollectionPromoPage]: {
    title: 'Promo Page',
    images: [],
  },
};

const COMPONENT_TYPE_OPTIONS = [
  HomepageCollectionComponentType.CollectionBanner,
  HomepageCollectionComponentType.CollectionCarousel,
  HomepageCollectionComponentType.CollectionHighlightCarousel,
];

const ALLOWED_POSITIONS: Record<string, string[]> = {
  [HomepageCollectionComponentType.CollectionBanner]: ['1A', '1B'],
  [HomepageCollectionComponentType.CollectionCarousel]: ['4', '6'],
  [HomepageCollectionComponentType.CollectionHighlightCarousel]: [
    '3',
    '5',
    '7',
  ],
};

export default function AddCollectionToHomepage() {
  const client = useClient();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId') ?? '';
  const componentType = searchParams.get('componentType') ?? '';
  const rowId = searchParams.get('rowId') ?? '';
  const componentId = searchParams.get('componentId') ?? '';
  const masterHomepageId = searchParams.get('masterHomepageId') ?? '';
  const isMasterHomepageRowFlow = !!rowId;
  const setCollectionId = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('collectionId', value);
          } else {
            next.delete('collectionId');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setComponentType = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('componentType', value);
          } else {
            next.delete('componentType');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const [, setPendingFilesVersion] = useState(0);

  const handleFileSelect = useCallback(
    (fieldName: string, file: File | null) => {
      if (file) {
        pendingFilesRef.current.set(fieldName, file);
      } else {
        pendingFilesRef.current.delete(fieldName);
      }
      setPendingFilesVersion((v) => v + 1);
    },
    [],
  );

  const [{ data, fetching }] = useGetCollectionsPageQuery({
    requestPolicy: 'cache-and-network',
  });
  const [{ data: warehousesData, fetching: warehousesFetching }] =
    useGetWarehousesQuery({ requestPolicy: 'cache-and-network' });
  const [{ data: setupData }] = useGetCollectionSetupForEditQuery({
    pause: !collectionId,
    variables: { collectionId },
    requestPolicy: 'cache-and-network',
  });
  const [{ data: masterHomepageData, fetching: masterHomepageFetching }] =
    useGetMasterHomepageForEditQuery({
      pause: !isMasterHomepageRowFlow || !masterHomepageId,
      variables: { id: masterHomepageId },
      requestPolicy: 'cache-and-network',
    });
  const itemIds = useMemo(
    () => setupData?.collectionSetupForEdit?.itemIds ?? [],
    [setupData],
  );
  const { edges: itemEdges } = useAllCollectionItems(itemIds);
  const [, createComponent] = useCreateHomepageComponentMutation();

  const itemWarehouseCounts = useMemo(() => {
    const countMap = new Map<string, number>();
    itemEdges
      .map((edge) => edge?.node)
      .filter(Boolean)
      .forEach((node) => {
        const names = (
          'warehouseAssociation' in node!
            ? node!.warehouseAssociation
                ?.map((wa) => wa?.warehouse.name)
                .filter((n): n is string => !!n) ?? []
            : []
        ) as string[];
        names.forEach((n) => countMap.set(n, (countMap.get(n) ?? 0) + 1));
      });
    return countMap;
  }, [itemEdges]);

  const collections = useMemo(
    () =>
      data?.collectionsPage
        ?.map((entry) => entry.collection)
        .filter(
          (c): c is { id: string; name: string } => !!c?.id && !!c?.name,
        ) ?? [],
    [data],
  );
  const warehouses = useMemo(
    () =>
      (warehousesData || { warehouses: [] }).warehouses
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [warehousesData],
  );
  const masterHomepage = masterHomepageData?.masterHomepageForEdit ?? null;
  const selectedRow =
    masterHomepage?.rows.find((row) => row.id === rowId) ?? null;
  const selectedRowComponent =
    selectedRow?.components.find(
      (component) => component.catalogComponentId === componentId,
    ) ?? null;
  const isBanner =
    componentType === HomepageCollectionComponentType.CollectionBanner;
  const requiresSide = isMasterHomepageRowFlow && isBanner;

  const initialValues = useMemo<CreativeAssetFormData>(() => {
    if (isMasterHomepageRowFlow) {
      return {
        image: selectedRowComponent?.image ?? '',
        webPosition: selectedRowComponent?.sortOrder
          ? String(selectedRowComponent.sortOrder)
          : '',
        mobileImage: selectedRowComponent?.mobileImage ?? '',
        desktopImage: selectedRowComponent?.desktopImage ?? '',
        warehouseIds: selectedRowComponent?.warehouseIds ?? [],
        webEndDate: String(selectedRowComponent?.endDate ?? ''),
        webStartDate: String(selectedRowComponent?.startDate ?? ''),
        fontStyle: selectedRowComponent?.fontStyle?.toUpperCase() ?? '',
      };
    }
    const match = setupData?.collectionSetupForEdit?.homepageComponents?.find(
      (c) => c.componentType === componentType,
    );
    if (match) {
      const enabledAttrs = match.warehouseAttributes.filter(
        (wa) => wa.enabled !== false,
      );
      const firstAttr = match.warehouseAttributes[0];
      const rule = setupData?.collectionSetupForEdit?.displayRules?.find(
        (r) => r.componentType === componentType,
      );
      return {
        image: firstAttr?.image ?? '',
        webPosition: rule?.position ?? '',
        webEndDate: String(rule?.endDate ?? ''),
        mobileImage: firstAttr?.mobileImage ?? '',
        webStartDate: String(rule?.startDate ?? ''),
        desktopImage: firstAttr?.desktopImage ?? '',
        fontStyle: firstAttr?.fontStyle?.toUpperCase() ?? '',
        warehouseIds:
          rule?.warehouseIds ?? enabledAttrs.map((wa) => wa.warehouse.id),
      };
    }
    return {
      image: '',
      fontStyle: '',
      webEndDate: '',
      mobileImage: '',
      webPosition: '',
      desktopImage: '',
      webStartDate: '',
      warehouseIds: [],
    };
  }, [componentType, isMasterHomepageRowFlow, selectedRowComponent, setupData]);

  const existingComponent = isMasterHomepageRowFlow
    ? selectedRowComponent
      ? { componentId: selectedRowComponent.catalogComponentId }
      : undefined
    : setupData?.collectionSetupForEdit?.homepageComponents?.find(
        (c) => c.componentType === componentType,
      );
  const existingRule = isMasterHomepageRowFlow
    ? undefined
    : setupData?.collectionSetupForEdit?.displayRules?.find(
        (r) => r.componentType === componentType,
      );

  const navigateToCollections = useCallback(() => {
    if (masterHomepageId) {
      navigate(
        `/collections?tab=catalog-homepage&masterHomepageId=${masterHomepageId}`,
      );
      return;
    }
    navigate('/collections');
  }, [masterHomepageId, navigate]);

  if (fetching || warehousesFetching || masterHomepageFetching) {
    return (
      <Box sx={{ paddingTop: isDevelopment ? 35.5 : 0 }}>
        <LoadingBox />
      </Box>
    );
  }

  const showCreativeAsset = collectionId && componentType;
  const config =
    IMAGES_BY_COMPONENT[componentType as HomepageCollectionComponentType];

  return (
    <Form<CreativeAssetFormData>
      // eslint-disable-next-line complexity
      onSubmit={async (values, form) => {
        setSaving(true);
        try {
          // Upload pending files before mutation
          const updatedValues = { ...values };
          try {
            // Upload sequentially to avoid urql deduplicating identical in-flight queries
            for (const [fieldName, file] of pendingFilesRef.current.entries()) {
              const key = await uploadImage(client, file);
              form.change(fieldName as keyof CreativeAssetFormData, key);
              (updatedValues as any)[fieldName] = key;
            }
          } catch (err) {
            dispatch(
              showToast({
                header: 'Image upload failed',
                alertType: ToastAlertType.ERROR,
                content: err instanceof Error ? err.message : 'Upload failed',
              }),
            );
            return;
          }

          // Single upsert mutation handles both component + display rule
          const input: CreateHomepageComponentInput = {
            collectionId,
            warehouseIds: updatedValues.warehouseIds,
            fontStyle: updatedValues.fontStyle || undefined,
            highlightImage: updatedValues.image || undefined,
            mobileImage: updatedValues.mobileImage || undefined,
            desktopImage: updatedValues.desktopImage || undefined,
          };
          if (isMasterHomepageRowFlow) {
            input.rowId = rowId;
            input.componentId = existingComponent?.componentId || undefined;
            input.position = isBanner ? updatedValues.webPosition : '1';
            input.displayRule = {
              endDate: updatedValues.webEndDate,
              startDate: updatedValues.webStartDate,
            };
          } else {
            input.position = updatedValues.webPosition;
            input.displayRule = {
              endDate: updatedValues.webEndDate,
              startDate: updatedValues.webStartDate,
              warehouseIds: updatedValues.warehouseIds,
              ruleId: existingRule?.ruleId || undefined,
            };
            input.componentId = existingComponent?.componentId || undefined;
          }
          const { data: compData } = await createComponent({ input });
          const errors = compData?.createHomepageComponent?.errors ?? [];
          if (errors.length > 0) {
            dispatch(
              showToast({
                alertType: ToastAlertType.ERROR,
                header: 'Error saving component',
                content: errors.map((event) => event.message).join(', '),
              }),
            );
            return;
          }

          dispatch(
            showToast({
              alertType: ToastAlertType.SUCCESS,
              header: isMasterHomepageRowFlow
                ? existingComponent
                  ? 'Collection updated in row!'
                  : 'Collection added to row!'
                : existingRule
                ? 'Component updated!'
                : 'Collection added to homepage!',
              content: isMasterHomepageRowFlow
                ? existingComponent
                  ? 'The collection has been updated in the selected row.'
                  : 'The collection has been attached to the selected row.'
                : existingRule
                ? 'The component and display rule have been updated.'
                : 'The component and display rule have been created.',
            }),
          );
          navigateToCollections();
        } finally {
          setSaving(false);
        }
      }}
      initialValues={initialValues}
      key={`${collectionId}-${componentType}-${
        existingComponent?.componentId ?? ''
      }`}
      // eslint-disable-next-line complexity
      render={({ form, values, handleSubmit }) => (
        <Flex
          sx={{
            inset: 0,
            position: 'fixed',
            overflow: 'hidden',
            flexDirection: 'column',
            paddingTop: isDevelopment ? 35.5 : 0,
          }}
        >
          <Flex
            id="navigation"
            sx={{
              px: 6,
              height: 80,
              alignItems: 'center',
              borderBottom: '1px solid',
              justifyContent: 'space-between',
              borderColor: 'secondaryHighlight',
            }}
          >
            <Flex sx={{ gap: 4, alignItems: 'center' }}>
              <Button
                variant="ghost"
                iconLeft={<IconClose />}
                onPress={() =>
                  window.history.length > 1
                    ? navigate(-1)
                    : navigateToCollections()
                }
              />
              <Box
                sx={{
                  height: 40,
                  width: '1px',
                  backgroundColor: 'secondaryHighlight',
                }}
              />
              <Heading variant="headingSm" sx={{ mt: 1 }}>
                {isMasterHomepageRowFlow
                  ? 'Add Collection to Row'
                  : 'Add Collection to Homepage'}
              </Heading>
            </Flex>
            <Button
              variant="primary"
              isDisabled={saving}
              onPress={() => {
                setSubmitted(true);
                const hasPosition = isMasterHomepageRowFlow
                  ? !isBanner || !!values.webPosition
                  : !!values.webPosition;
                const hasLaunchFields =
                  values.webStartDate &&
                  values.webEndDate &&
                  values.webEndDate > values.webStartDate &&
                  hasPosition &&
                  (!isBanner || values.fontStyle);
                if (collectionId && componentType && hasLaunchFields) {
                  handleSubmit();
                }
              }}
            >
              {saving ? 'Saving...' : 'Confirm'}
            </Button>
          </Flex>
          <Flex
            id="content"
            sx={{
              p: 6,
              gap: 6,
              flex: 1,
              overflowY: 'auto',
              flexDirection: 'column',
            }}
          >
            <Heading id="title" variant="headingMd">
              Creative Asset Type and Position
            </Heading>
            {isMasterHomepageRowFlow && (
              <>
                <Heading variant="headingMd">Homepage Context</Heading>
                <Grid sx={{ gap: 3, gridTemplateColumns: ['1fr', '1fr 1fr'] }}>
                  <Box>
                    <Heading variant="headingXs">Homepage</Heading>
                    <Input
                      value={masterHomepage?.name ?? 'Unknown homepage'}
                      disabled
                    />
                  </Box>
                  <Box>
                    <Heading variant="headingXs">Row</Heading>
                    <Input
                      value={
                        selectedRow
                          ? `Row ${selectedRow.sortOrder}${
                              selectedRow.structureType ===
                              MasterHomepageRowStructureType.CollectionBanner
                                ? 'A-B'
                                : ''
                            }: ${formatMasterHomepageRowType(
                              selectedRow.structureType,
                            )}`
                          : 'Unknown row'
                      }
                      disabled
                    />
                  </Box>
                </Grid>
              </>
            )}
            <Grid sx={{ gap: 3, gridTemplateColumns: ['1fr', '1fr 1fr'] }}>
              <Box>
                <Heading variant="headingXs">Choose Collection</Heading>
                <Select
                  disabled={saving}
                  value={collectionId}
                  onChange={(event) => setCollectionId(event.target.value)}
                  sx={{
                    height: '40px',
                    borderColor:
                      submitted && !collectionId
                        ? 'errorDefault'
                        : 'secondaryHighlight',
                  }}
                >
                  <option value="">Select a collection...</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <ErrorLabel
                  message={submitted && !collectionId ? 'Required' : undefined}
                />
              </Box>
              {isMasterHomepageRowFlow ? (
                <Box>
                  <Heading variant="headingXs">Component</Heading>
                  <Input
                    value={
                      componentType
                        ? formatComponentType(
                            componentType as HomepageCollectionComponentType,
                          )
                        : 'Missing component type'
                    }
                    disabled
                  />
                </Box>
              ) : (
                <Box>
                  <Heading variant="headingXs">Choose Component</Heading>
                  <Select
                    disabled={saving}
                    value={componentType}
                    onChange={(event) => setComponentType(event.target.value)}
                    sx={{
                      height: '40px',
                      borderColor:
                        submitted && !componentType
                          ? 'errorDefault'
                          : 'secondaryHighlight',
                    }}
                  >
                    <option value="">Select a component...</option>
                    {COMPONENT_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {formatComponentType(type)}
                      </option>
                    ))}
                  </Select>
                  <ErrorLabel
                    message={
                      submitted && !componentType ? 'Required' : undefined
                    }
                  />
                </Box>
              )}
              {requiresSide && (
                <Box>
                  <Heading variant="headingXs">Choose Side</Heading>
                  <Select
                    disabled={saving}
                    value={values.webPosition}
                    onChange={(event) =>
                      form.change('webPosition', event.target.value)
                    }
                    sx={{
                      height: '40px',
                      borderColor:
                        submitted && !values.webPosition
                          ? 'errorDefault'
                          : 'secondaryHighlight',
                    }}
                  >
                    <option value="">Select a side...</option>
                    <option value="1">A</option>
                    <option value="2">B</option>
                  </Select>
                  <ErrorLabel
                    message={
                      submitted && !values.webPosition ? 'Required' : undefined
                    }
                  />
                </Box>
              )}
            </Grid>
            {showCreativeAsset && (
              <>
                <CreativeAsset
                  disabled={saving}
                  title={config.title}
                  warehouses={warehouses}
                  onFileSelect={handleFileSelect}
                  onWarehouseIdsChange={(ids) =>
                    form.change('warehouseIds', ids)
                  }
                  warehouseIds={values.warehouseIds}
                  pendingFiles={pendingFilesRef.current}
                  images={config.images.map((img) => ({
                    ...img,
                    value: values[img.fieldName] as string,
                  }))}
                  itemWarehouseCounts={itemWarehouseCounts}
                  onImageChange={(fieldName, key) => {
                    form.change(fieldName as keyof CreativeAssetFormData, key);
                  }}
                />
                {!isMasterHomepageRowFlow && (
                  <Heading variant="headingMd">Configure Launch</Heading>
                )}
                <Grid sx={{ gap: 6, gridTemplateColumns: ['1fr', '1fr 1fr'] }}>
                  <ConfigureLaunch
                    label="Web & Mobile"
                    disabled={saving}
                    submitted={submitted}
                    endDate={values.webEndDate}
                    position={values.webPosition}
                    startDate={values.webStartDate}
                    onStartDateChange={(date) => {
                      form.change('webStartDate', date);
                      if (
                        !date ||
                        !values.webEndDate ||
                        values.webEndDate > date
                      ) {
                        return;
                      }
                      form.change('webEndDate', '');
                    }}
                    hidePosition={isMasterHomepageRowFlow}
                    allowedPositions={
                      isMasterHomepageRowFlow
                        ? []
                        : ALLOWED_POSITIONS[componentType] ?? []
                    }
                    fontStyle={
                      componentType ===
                      HomepageCollectionComponentType.CollectionBanner
                        ? values.fontStyle
                        : undefined
                    }
                    onFontStyleChange={
                      componentType ===
                      HomepageCollectionComponentType.CollectionBanner
                        ? (style) => form.change('fontStyle', style)
                        : undefined
                    }
                    onEndDateChange={(date) => form.change('webEndDate', date)}
                    onPositionChange={(pos) => form.change('webPosition', pos)}
                  />
                  <div />
                </Grid>
              </>
            )}
          </Flex>
        </Flex>
      )}
    />
  );
}
